import React, { FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useUIContext } from 'state/ui';
import { CartEntryType } from 'utils/cart';

import { RecentOrderCardWithModifiersView } from './recent-order-card-with-modifiers.view';
import { IRecentOrderCardWithModifiersProps } from './types';

const messages = defineMessages({
  notAvailableAtStore: {
    id: 'notAvailableAtStore',
    defaultMessage: 'Not available',
    description: 'No description'
  },
  addToCart: {
    id: 'add',
    defaultMessage: 'Add',
    description: 'Add to cart'
  },
  amountMore: {
    id: 'amountMore',
    default: '+{amountMore} more',
    description: 'Text informing guest how many more modifiers are not shown'
  }
});

/**
 *
 * RecentOrderCardWithModifiers is an alternative version of
 * the RecentOrderCard that displays extra information including
 * the item's modifiers. This is particularly useful for items
 * such as Coffee. Without modifiers visible, its hard to know much
 * about the coffee order.
 *
 */
const RecentOrderCardWithModifiers: FC<IRecentOrderCardWithModifiersProps> = ({
  productImage,
  backgroundImage,
  item,
  handleAddClick,
  isAvailable
}) => {
  const { formatMessage } = useIntl();
  const modifiers = item.children ?? [];

  const firstTwoModifiers = modifiers.slice(0, 2).map(modifier => {
    let { name } = modifier;
    if (modifier.type === CartEntryType.itemOption && modifier.children?.[0]) {
      name = modifier.children[0].name || name;
    }
    return name ?? '';
  });
  const amountMore = modifiers.length - 2;
  const moreText = formatMessage(messages.amountMore, { amountMore });
  const moreModifiers = amountMore > 0 ? [moreText] : [];
  const addButtonText = formatMessage(messages.addToCart);
  const unavailableMessage = formatMessage(messages.notAvailableAtStore);
  const { buildImageUrl } = useUIContext();

  return (
    <RecentOrderCardWithModifiersView
      name={item.name ?? ''}
      isAvailable={isAvailable && !item.isDisabled}
      productImage={productImage}
      backgroundImage={backgroundImage}
      handleAddClick={handleAddClick}
      unavailableMessage={unavailableMessage}
      addButtonText={addButtonText}
      modifiers={[...firstTwoModifiers, ...moreModifiers]}
      buildImageUrl={buildImageUrl}
    />
  );
};

export default RecentOrderCardWithModifiers;
